import React from 'react'
import {BrowserRouter as Router, Route, Switch, Redirect} from 'react-router-dom'
import {LeagueList} from './routes/league-list'
import {LeagueView} from './routes/league-view'
import {PlayersNew} from './routes/players-new'
import {GamesNew} from './routes/games-new'

import clsx from 'clsx'
import {makeStyles} from '@material-ui/core/styles'
import CssBaseline from '@material-ui/core/CssBaseline'
import Drawer from '@material-ui/core/Drawer'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import List from '@material-ui/core/List'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import Container from '@material-ui/core/Container'
import MenuIcon from '@material-ui/icons/Menu'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import DashboardIcon from '@material-ui/icons/Dashboard'
import TableOfContentsIcon from '@material-ui/icons/Toc'
import PeopleIcon from '@material-ui/icons/People'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import BarChartIcon from '@material-ui/icons/BarChart'
import {ILeagueRouteParams} from './types'
import {GamesView} from './routes/games-view'
import {GamesList} from './routes/games-list'

const drawerWidth = 240

const LEAGUES_REGEX = /\/leagues\/\w+\//

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 10,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    flexGrow: 1,
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  fixedHeight: {
    height: 240,
  },
  disabledMenuItem: {
    opacity: 0.5,
  },
}))

function LorumApp(props: ILeagueRouteParams) {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const handleDrawerOpen = () => setOpen(true)
  const handleDrawerClose = () => setOpen(false)
  const isInLeague = LEAGUES_REGEX.test(props.location.pathname)
  const [leaguePortion] = props.location.pathname.match(LEAGUES_REGEX) || ['']

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open drawer"
            onClick={handleDrawerOpen}
            className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
          >
            <MenuIcon />
          </IconButton>
          <Typography component="h1" variant="h6" color="inherit" noWrap className={classes.title}>
            Lorum
          </Typography>
          <IconButton color="inherit" onClick={() => props.history.goBack()}>
            <ArrowBackIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
        }}
        open={open}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <div>
            <ListItem button>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText
                primary="Home"
                onClick={() => {
                  handleDrawerClose()
                  props.history.push('/')
                }}
              />
            </ListItem>
            <ListItem button>
              <ListItemIcon>
                <TableOfContentsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Games"
                className={clsx({[classes.disabledMenuItem]: !isInLeague})}
                onClick={() => {
                  if (!isInLeague) return
                  handleDrawerClose()
                  props.history.push(`${leaguePortion}games/list`)
                }}
              />
            </ListItem>
            <ListItem button>
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Add Player"
                className={clsx({[classes.disabledMenuItem]: !isInLeague})}
                onClick={() => {
                  if (!isInLeague) return
                  handleDrawerClose()
                  props.history.push(`${leaguePortion}players/new`)
                }}
              />
            </ListItem>
            <ListItem button style={{display: 'none'}}>
              <ListItemIcon>
                <BarChartIcon />
              </ListItemIcon>
              <ListItemText primary="Statistics" />
            </ListItem>
          </div>
        </List>
        <Divider />
      </Drawer>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Switch>
            <Route exact path="/leagues" component={LeagueList} />
            <Route exact path="/leagues/:slug/view" component={LeagueView} />
            <Route exact path="/leagues/:slug/players/new" component={PlayersNew} />
            <Route exact path="/leagues/:slug/games/new" component={GamesNew} />
            <Route exact path="/leagues/:slug/games/list" component={GamesList} />
            <Route exact path="/leagues/:slug/games/:gameId/view" component={GamesView} />
            <Redirect to="/leagues" />
          </Switch>
        </Container>
      </main>
    </div>
  )
}

function App() {
  return (
    <Router>
      <Route path="/" component={LorumApp} />
    </Router>
  )
}

export default App
